import React, { useState } from "react";
import { Loader2, Copy, Check, Send } from "lucide-react";
import { Button } from "@pgs-core/ui/components/button";
import type { ApiClient } from "../lib/api";

export interface InviteDetails {
  userId: string;
  email: string;
  link: string | null;
}

interface InviteLinkBoxProps {
  api: ApiClient;
  clientId: string;
  invite: InviteDetails;
  /** Sending regenerates the one-time link; the fresh one replaces the shown link. */
  onLinkChange: (link: string) => void;
}

// One-time invite link panel shown after creating a user or clicking Invite:
// the link itself plus Copy and Send-by-email actions. Shared by UsersPanel
// and AddPortalUserForm so the two invite flows stay identical.
export function InviteLinkBox({ api, clientId, invite, onLinkChange }: InviteLinkBoxProps) {
  const [copied, setCopied] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const copyLink = async () => {
    if (!invite.link) return;
    try {
      await navigator.clipboard.writeText(invite.link);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  const sendByEmail = async () => {
    setSending(true);
    setError("");
    try {
      const body = await api.request<{ sent: boolean; inviteLink: string }>(
        `/admin/clients/${clientId}/users/${invite.userId}/invite/email`,
        { method: "POST" }
      );
      // Emailing mints a fresh one-time link and invalidates the shown one.
      onLinkChange(body.inviteLink);
      setSent(true);
      setCopied(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "The invite email could not be sent");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="mt-3 rounded-lg border border-success/40 bg-success/5 p-3.5 text-sm">
      <p className="font-medium">Invite link for {invite.email}</p>
      <p className="mt-0.5 text-xs text-muted-foreground">
        {sent
          ? `Invite emailed to ${invite.email} — the link below is the fresh one it contains.`
          : "Send this to the client so they can set their password. It won't be shown again."}
      </p>
      <div className="mt-2 flex items-center gap-2">
        <code className="min-w-0 flex-1 truncate rounded bg-muted px-2 py-1.5 font-mono text-xs">
          {invite.link ?? "No link generated"}
        </code>
        {invite.link && (
          <Button size="sm" variant="outline" onClick={copyLink}>
            {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
            {copied ? "Copied" : "Copy"}
          </Button>
        )}
        {invite.link && (
          <Button size="sm" variant="outline" disabled={sending} onClick={sendByEmail}>
            {sending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : sent ? (
              <Check className="h-3.5 w-3.5" />
            ) : (
              <Send className="h-3.5 w-3.5" />
            )}
            {sending ? "Sending…" : sent ? "Sent" : "Send by email"}
          </Button>
        )}
      </div>
      {error && <p className="mt-2 text-xs text-destructive">{error}</p>}
    </div>
  );
}
